import React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { financialRecordsApi } from "../../services/api";
import { formatCurrency, formatDate } from "../../utils/formatters";
import {
  BankTransaction,
  FinancialRecord,
} from "@akfdev-com/shared/src/models";

/**
 * Read-only counterpart to ExplainTransactionForm for a transaction that has
 * ALREADY been explained (reconciled). It renders in the exact same visual
 * language as the explain form — info-tint panel, type chip, selected-item
 * chip, and the fully-allocated summary — so an explained row looks and reads
 * consistently with an unexplained one (Alt 1 plan).
 *
 * The linked FinancialRecord is fetched to show its name and amount; a "View"
 * link resolves to the originating record. Changing/removing a posted
 * explanation (unmatch + reverse) is reconciliation Requirement 6 — a backend
 * follow-up — so this view is inspect-only for now.
 */

interface LinkInfo {
  recordId?: string;
  typeLabel: string;
  postingHint: string;
}

function resolveLink(tx: BankTransaction): LinkInfo {
  if (tx.linkedBillId) {
    return {
      recordId: tx.linkedBillId,
      typeLabel: "Bill payment",
      postingHint: "Dr Trade Creditors / Cr Bank",
    };
  }
  if (tx.linkedServiceChargeId) {
    return {
      recordId: tx.linkedServiceChargeId,
      typeLabel: "Service charge receipt",
      postingHint: "Dr Bank / Cr Service Charge Debtors",
    };
  }
  if (tx.linkedInvoiceId) {
    return {
      recordId: tx.linkedInvoiceId,
      typeLabel: "Invoice receipt",
      postingHint: "Dr Bank / Cr Trade Debtors",
    };
  }
  // Generic link (older reconciliations set only financialRecordId).
  const moneyIn = tx.grossValue >= 0;
  return {
    recordId: tx.financialRecordId,
    typeLabel: moneyIn ? "Receipt" : "Payment",
    postingHint: moneyIn ? "Dr Bank / Cr control" : "Dr control / Cr Bank",
  };
}

function recordDisplayName(record: FinancialRecord): string {
  return (
    record.contactName ||
    record.supplierName ||
    record.unitName ||
    record.reference ||
    record.title ||
    "Linked record"
  );
}

export interface ExplainedTransactionSummaryProps {
  transaction: BankTransaction;
  buildingId: string;
  currency?: string;
  variant?: "inline" | "page";
  /** Inline variant only: link to the full-page explanation view. */
  fullPageHref?: string;
  /** Called after an allocation is successfully unmatched (to refetch). */
  onUnmatched?: () => void;
}

const ExplainedTransactionSummary: React.FC<
  ExplainedTransactionSummaryProps
> = ({
  transaction,
  buildingId,
  currency = "GBP",
  variant = "inline",
  fullPageHref,
  onUnmatched,
}) => {
  const direction = transaction.grossValue >= 0 ? "in" : "out";
  const amount = Math.abs(transaction.grossValue);
  const { recordId, typeLabel, postingHint } = resolveLink(transaction);
  const allocations = transaction.allocations ?? [];
  const recordHref = recordId
    ? `/dashboard/financial-records/${recordId}?buildingId=${buildingId}`
    : undefined;

  const [unmatchingId, setUnmatchingId] = useState<string | null>(null);

  // Unmatch one allocation: reverses its Settlement, restores the item, and
  // re-opens the line (reconciliation Req 6). `targetRecordId` is the item to
  // unlink — for a split, each allocation is unmatched independently.
  const handleUnmatch = async (targetRecordId?: string) => {
    if (!targetRecordId) return;
    setUnmatchingId(targetRecordId);
    try {
      await financialRecordsApi.unreconcile(
        targetRecordId,
        buildingId,
        transaction.id,
        transaction.buildingId,
      );
      toast.success(
        "Explanation removed. The transaction can be matched again.",
      );
      onUnmatched?.();
    } catch (err) {
      console.error("Unmatch failed:", err);
      toast.error("Could not remove this explanation. Please try again.");
    } finally {
      setUnmatchingId(null);
    }
  };

  const { data: record } = useQuery<FinancialRecord>({
    queryKey: ["financial-record", recordId, buildingId],
    queryFn: () => financialRecordsApi.get(recordId!, buildingId),
    enabled: !!recordId && !!buildingId,
  });

  const amountColor = direction === "in" ? "text-green-700" : "text-red-600";
  const containerClass =
    variant === "page"
      ? "space-y-5"
      : "rounded-lg border border-blue-200 bg-blue-50/60 p-4 space-y-4";

  return (
    <div className={containerClass} data-testid="explained-transaction-summary">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Explained {direction === "in" ? "receipt" : "payment"}
          </p>
          <p className="mt-0.5 text-sm font-medium text-gray-900">
            {transaction.description}
          </p>
        </div>
        <div className={`text-right font-mono font-semibold ${amountColor}`}>
          {formatCurrency(transaction.grossValue / 100, currency)}
        </div>
      </div>

      {/* Type + matched status (mirrors the explain form's type row) */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Type
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-md bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white">
            {typeLabel}
          </span>
          <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
            Matched
          </span>
          <span className="text-xs text-gray-500">{postingHint}</span>
        </div>
      </div>

      {/* Allocated against — a single item, or several when the line was split. */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          {allocations.length > 1
            ? `Allocated across ${allocations.length} items`
            : "Allocated against"}
        </p>

        {allocations.length > 1 ? (
          <ul className="mt-1 space-y-2">
            {allocations.map((a, i) => (
              <li
                key={`${a.financialRecordId}-${i}`}
                className="flex items-center justify-between rounded-md border border-blue-200 bg-white px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900">
                    {a.itemType
                      ? a.itemType.replace("_", " ")
                      : "Linked record"}
                  </span>
                  <span className="block truncate text-xs text-gray-500">
                    {formatCurrency((a.amount || 0) / 100, currency)}
                  </span>
                </span>
                <span className="ml-3 flex shrink-0 items-center gap-3">
                  <Link
                    to={`/dashboard/financial-records/${a.financialRecordId}?buildingId=${buildingId}`}
                    className="text-sm font-medium text-primary-600 hover:underline"
                  >
                    View &rarr;
                  </Link>
                  <button
                    type="button"
                    disabled={unmatchingId === a.financialRecordId}
                    onClick={() => handleUnmatch(a.financialRecordId)}
                    className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    {unmatchingId === a.financialRecordId
                      ? "Unmatching…"
                      : "Unmatch"}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-1 flex items-center justify-between rounded-md border border-blue-200 bg-white px-3 py-2">
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-gray-900">
                {record ? recordDisplayName(record) : "Linked record"}
              </span>
              <span className="block truncate text-xs text-gray-500">
                {record
                  ? [record.reference, record.date]
                      .filter(Boolean)
                      .join(" \u00b7 ")
                  : transaction.paidInvoiceReference ||
                    transaction.paidBillReference ||
                    transaction.reference ||
                    "\u2014"}
              </span>
            </span>
            {recordHref && (
              <Link
                to={recordHref}
                className="ml-3 shrink-0 text-sm font-medium text-primary-600 hover:underline"
              >
                View &rarr;
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Allocation summary (identical layout to the explain form) */}
      <div className="rounded-md border border-gray-200 bg-white px-3 py-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">
            {direction === "in" ? "Receipt" : "Payment"} amount
          </span>
          <span className="font-mono text-gray-900">
            {formatCurrency(amount / 100, currency)}
          </span>
        </div>
        <div className="mt-1 flex justify-between">
          <span className="text-gray-500">Allocated</span>
          <span className="font-mono text-gray-900">
            {formatCurrency(amount / 100, currency)}
          </span>
        </div>
        <div className="mt-2 flex justify-between border-t border-gray-100 pt-2">
          <span className="font-semibold text-gray-900">
            Remaining to explain
          </span>
          <span className="font-mono font-semibold text-green-700">
            {formatCurrency(0, currency)}
          </span>
        </div>
      </div>

      {/* Actions — full-page affordance + unmatch (single case; splits unmatch
          per allocation row above) */}
      <div className="flex flex-wrap items-center gap-2">
        {variant === "inline" && fullPageHref && (
          <Link
            to={fullPageHref}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Open full page
          </Link>
        )}
        {allocations.length <= 1 && recordId && (
          <button
            type="button"
            disabled={unmatchingId === recordId}
            onClick={() => handleUnmatch(recordId)}
            className="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            {unmatchingId === recordId ? "Unmatching…" : "Unmatch"}
          </button>
        )}
      </div>

      {/* Provenance line (compact, replaces the old detail grid) */}
      <p className="text-xs text-gray-400">
        {[
          transaction.nominalCode && `Nominal ${transaction.nominalCode}`,
          transaction.categoryName,
          transaction.updatedAt &&
            `Reconciled ${formatDate(transaction.updatedAt)}`,
        ]
          .filter(Boolean)
          .join(" \u00b7 ") || "Settlement posted to the ledger."}
      </p>
    </div>
  );
};

export default ExplainedTransactionSummary;
